<script setup>

import { computed, nextTick, onMounted, ref, watch } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
    propSchedule: { type: Object, required: true },
    showValidationErrors: { type: Boolean, default: false },
})

const schedule = ref({ ...props.propSchedule })
// const propSchedule = computed(() => {
//     return props.propSchedule;
// })

// eslint-disable-next-line no-undef
const emit = defineEmits(['changeSchedule']);

const lastValidTimes = ref();
const isFocused = ref(true);

const timeEnterMorningRef = ref(null);
const timeExitMorningRef = ref(null);
const timeEnterAfternoonRef = ref(null);
const timeExitAfternoonRef = ref(null);

const toMinutes = (time) => {
    if (!time) {
        return null;
    }

    const [hours, minutes] = String(time).split(':').map(Number);
    return (hours * 60) + minutes;
};

const timeErrors = computed(() => {
    const errors = {};
    const morningStart = toMinutes(schedule.value.timeEnterMorning);
    const morningEnd = toMinutes(schedule.value.timeExitMorning);
    const afternoonStart = toMinutes(schedule.value.timeEnterAfternoon);
    const afternoonEnd = toMinutes(schedule.value.timeExitAfternoon);

    if (schedule.value.timeEnterMorning && !schedule.value.timeExitMorning) {
        errors.timeExitMorning = 'Debe indicar la hora de salida de mañana';
    }
    if (!schedule.value.timeEnterMorning && schedule.value.timeExitMorning) {
        errors.timeEnterMorning = 'Debe indicar la hora de entrada de mañana';
    }
    if (schedule.value.timeEnterAfternoon && !schedule.value.timeExitAfternoon) {
        errors.timeExitAfternoon = 'Debe indicar la hora de salida de tarde';
    }
    if (!schedule.value.timeEnterAfternoon && schedule.value.timeExitAfternoon) {
        errors.timeEnterAfternoon = 'Debe indicar la hora de entrada de tarde';
    }
    if (morningStart !== null && morningEnd !== null && morningStart >= morningEnd) {
        errors.timeExitMorning = 'La salida debe ser posterior a la entrada';
    }
    if (afternoonStart !== null && afternoonEnd !== null && afternoonStart >= afternoonEnd) {
        errors.timeExitAfternoon = 'La salida debe ser posterior a la entrada';
    }

    return errors;
});

onMounted(() => {
    nextTick();
    lastValidTimes.value = {
        timeEnterMorning: schedule.value.timeEnterMorning,
        timeExitMorning: schedule.value.timeExitMorning,
        timeEnterAfternoon: schedule.value.timeEnterAfternoon,
        timeExitAfternoon: schedule.value.timeExitAfternoon,
    }
    isFocused.value = false;
})

watch(() => props.propSchedule, (newVal) => {
    isFocused.value = true;
    const days = [
        { day: 'mon', workToday: newVal.mon },
        { day: 'tue', workToday: newVal.tue },
        { day: 'wed', workToday: newVal.wed },
        { day: 'thu', workToday: newVal.thu },
        { day: 'fri', workToday: newVal.fri },
    ]
    schedule.value = { ...newVal, days };
    isFocused.value = false;
})

watch(schedule, (newVal) => {
    if (!isFocused.value) {
        emit('changeSchedule', newVal)
    }

}, { deep: true, immediate: true })

const focusInputHour = (key) => {
    isFocused.value = true;
    if (schedule.value[key]) {
        schedule.value[key] = schedule.value[key].replace(':', '')
    }
}

const changeInputHour = (key) => {
    let val = schedule.value[key];
    if (val === '' || val === null || typeof val === 'undefined') {
        schedule.value[key] = null;
        lastValidTimes.value[key] = null;
        isFocused.value = false;
        return;
    }

    val = val.replace(/\D/g, "");

    if (val.length === 1) {
        val = `0${val}:00`;
    } else if (val.length === 2) {
        if (parseInt(val, 10) > 23) {
            schedule.value[key] = lastValidTimes.value[key];
            return;
        }
        val = `${val}:00`;
    } else if (val.length === 3) {
        val = `0${val[0]}:${val.slice(1)}`;
    } else if (val.length >= 4) {
        let hours = parseInt(val.slice(0, 2), 10);
        let minutes = parseInt(val.slice(2, 4), 10);

        if (hours > 23 || minutes > 59) {
            schedule.value[key] = lastValidTimes.value[key];
            return;
        }

        val = `${hours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}`;
    }

    if (val === lastValidTimes.value[key]) {
        isFocused.value = true;
    } else {
        isFocused.value = false;
    }

    schedule.value[key] = val;
    lastValidTimes.value[key] = val;
    nextTick(() => {
        const inputRef = getInputRef(key);
        if (inputRef) {
            inputRef.blur();
        }
    });
}

const blurInputHour = (key) => {
    isFocused.value = false;
    changeInputHour(key);
}

const changeDay = (day) => {
    schedule.value[day.day] = day.workToday;
    emit('changeSchedule', schedule.value);
}

const trimDayText = (value) => {

    let text = '';
    switch (value) {
        case 'monday':
        case 'mon':
            text = 'Lun';
            break;
        case 'tuesday':
        case 'tue':
            text = 'Mar';
            break;
        case 'wednesday':
        case 'wed':
            text = 'Mie';
            break;
        case 'thursday':
        case 'thu':
            text = 'Jue';
            break;
        case 'friday':
        case 'fri':
            text = 'Vie';
            break;
        default:
            break;
    }
    return text;
}

const getInputRef = (key) => {
    switch (key) {
        case "timeEnterMorning":
            return timeEnterMorningRef.value;
        case "timeExitMorning":
            return timeExitMorningRef.value;
        case "timeEnterAfternoon":
            return timeEnterAfternoonRef.value;
        case "timeExitAfternoon":
            return timeExitAfternoonRef.value;
        default:
            return null;
    }
};



</script>

<template>
    <div>
        <div class="row d-flex justify-content-around" v-if="schedule">
            <div class="col-auto text-center col-day" v-for="(day, index) in schedule.days" :key="day.id">
                <label class="form-check-label" :for="'checkbox-' + index" style="color: black">
                    {{ trimDayText(day.day) }}
                </label>
                <br />
                <input type="checkbox" :id="'checkbox-' + index" v-model="day.workToday"
                    @change="changeDay(day)" />
            </div>
        </div>
        <hr>
        <div class="row item" style="margin: 10px">
            <label class="w-100">Horario de mañana</label>
            <el-input v-model="schedule.timeEnterMorning" ref="timeEnterMorningRef" placeholder="Horario entrada"
                class="mb-1" @focus="focusInputHour('timeEnterMorning', $event)"
                @change="changeInputHour('timeEnterMorning')" @blur="blurInputHour('timeEnterMorning')" />
            <small v-if="showValidationErrors && timeErrors.timeEnterMorning" class="invalid-feedback d-block">
                {{ timeErrors.timeEnterMorning }}
            </small>
            <el-input v-model="schedule.timeExitMorning" ref="timeExitMorning" placeholder="Horario salida" class="mb-1"
                @focus="focusInputHour('timeExitMorning', $event)" @change="changeInputHour('timeExitMorning')"
                @blur="blurInputHour('timeExitMorning')" />
            <small v-if="showValidationErrors && timeErrors.timeExitMorning" class="invalid-feedback d-block">
                {{ timeErrors.timeExitMorning }}
            </small>
            <label style="color: black; font-size: 18px;">Horario de tarde</label>
            <el-input v-model="schedule.timeEnterAfternoon" ref="timeEnterAfternoon" :label="'Horario entrada'"
                :placeholder="'Horario entrada'" class="mb-1" @focus="focusInputHour('timeEnterAfternoon', $event)"
                @change="changeInputHour('timeEnterAfternoon')" @blur="blurInputHour('timeEnterAfternoon')" />
            <small v-if="showValidationErrors && timeErrors.timeEnterAfternoon" class="invalid-feedback d-block">
                {{ timeErrors.timeEnterAfternoon }}
            </small>
            <el-input v-model="schedule.timeExitAfternoon" ref="timeExitAfternoon" :label="'Horario salida'"
                :placeholder="'Horario salida'" class="mb-1" @focus="focusInputHour('timeExitAfternoon', $event)"
                @change="changeInputHour('timeExitAfternoon')" @blur="blurInputHour('timeExitAfternoon')" />
            <small v-if="showValidationErrors && timeErrors.timeExitAfternoon" class="invalid-feedback d-block">
                {{ timeErrors.timeExitAfternoon }}
            </small>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@media (max-width: 457px) {
    .col-day{
        padding-right: 1px;
        padding-left: 6px;
    }
}
</style>
